<template>
  <b-card class="mb-3">
    <div class="d-flex flex-wrap justify-content-between">
      <h5 class="ml-4">Численность учащихся</h5>
      <div class="ml-auto mb-2">
        <b-btn
          variant="outline-primary"
          class="addButton align-items-center"
          v-on:click="onEditGridClicked"
          v-if="isEditVisible"
        >
          <em class="fas fa-edit mr-1"></em> Редактировать
        </b-btn>
        <b-btn
          variant="outline-primary"
          class="addButton align-items-center"
          v-on:click="onSaveGridClicked"
          v-if="!isEditVisible"
        >
          <em class="fas fa-check mr-1"></em> Сохранить изменения
        </b-btn>
      </div>
    </div>
    <b-alert variant="warning" show=""  v-if="!isEditVisible" class="warning-message">
      Вы находитесь в режиме редактирования. Не забудьте <strong>сохранить изменения</strong>!
    </b-alert>
    <ag-grid-vue
      class="ag-theme-material ag-grid-for-count-stat mt-3"
      :columnDefs="columnDefs"
      :gridOptions="gridOptions"
      :rowData="rowData"
      @grid-ready="onGridReady"
      @gridSizeChanged="onGridSizeChanged"
      @rowDataChanged = "onRowDataChanged"
      @column-resized="onColumnResized">
    </ag-grid-vue>
  </b-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { AgGridVue } from 'ag-grid-vue'
import _ from 'lodash'
import AG_GRID_LOCALE_RU from '@/config/locale.ru'
import StatsCountRenderer from '@/components/helpers/renderers/statistic/StatsCountRenderer'
import TitleRenderer from '@/components/helpers/renderers/statistic/TitleRenderer'
import statisticsApi from '@/api/statistics'

export default {
  label: 'ClassesStats',
  components: { AgGridVue },
  props: {
    loaded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      getYear: 0,
      selected: 0,
      options: [],
      selectedClass: [],
      loadedStatsDistribution: false,
      rowData: null,
      year: 0,
      context: {
        componentParent: this
      },
      isEditVisible: true,
      columnDefs: [],
      gridApi: null,
      gridOptions: {
        defaultColDef: {
          autoHeight: true,
          sortable: true,
          width: 120,
          headerClass: 'table-count-stats-header',
          lockPosition: true,
          suppressSizeToFit: true,
          cellRenderer: 'StatsCountRenderer'
        },
        getRowStyle(params) {
          if (params.node.rowIndex % 2 === 0) {
            return { background: '#f1f1f1' }
          }
          return {}
        },
        components: {
          StatsCountRenderer,
          TitleRenderer
        },
        localeText: AG_GRID_LOCALE_RU,
        headerHeight: 80,
        enableCellTextSelection: true,
        suppressMenuHide: true
      }
    }
  },
  async created() {
    this.getYear = (await statisticsApi.getStatisticYear()).data.year
    this.fetchStatsClassesStudentsDistribution().then(() => {
      this.options = this.orgs.filter((p) => p.type === 'ГП-класс')
        .map((gpClass) => ({
          value: gpClass.id,
          text: gpClass.title
        }))
      this.columnDefs = [
        {
          headerName: 'Класс',
          field: 'title',
          cellRenderer: 'TitleRenderer',
          suppressSizeToFit: false,
          width: 170,
          minWidth: 170
        },
        {
          headerName: `${this.getYear - 1} - ${this.getYear}`,
          field: 'current'
        },
        {
          headerName: `${this.getYear - 2} - ${this.getYear - 1}`,
          field: 'oneago'
        },
        {
          headerName: `${this.getYear - 3} - ${this.getYear - 2}`,
          field: 'twoago'
        },
        {
          headerName: `${this.getYear - 4} - ${this.getYear - 3}`,
          field: 'threeago'
        },
        {
          headerName: `${this.getYear - 5} - ${this.getYear - 4}`,
          field: 'fourago'
        }
      ]
      const tableData = this.filteredStudentsClassesDistribution
      tableData.forEach((td) => {
        const org = this.orgs.find((p) => p.id
          === td.organizationId && p.position)
        if (org) {
          td.title = org.title
          td.city = org.city
          td.fourago = td[this.getYear - 4]
          td.threeago = td[this.getYear - 3]
          td.twoago = td[this.getYear - 2]
          td.oneago = td[this.getYear - 1]
          td.current = td[this.getYear]
        } else {
          td.oldData = true
        }
      })
      this.rowData = tableData.filter((td) => !td.oldData)
      this.selected = this.options[0].value
      this.loadedStatsDistribution = true
    })
  },
  methods: {
    ...mapActions({
      fetchStatsClassesStudentsDistribution: 'statistics/fetchStatsClassesStudentsDistribution',
      updateStatsClassesStudentsDistribution: 'statistics/updateStatsClassesStudentsDistribution',
      pushToasts: 'toasts/pushToasts'
    }),
    async onEditGridClicked() {
      this.columnDefs = this.columnDefs
        .map((col) => (col.field === 'title' ? { ...col } : { ...col, editable: true }))
      await this.gridApi.resetRowHeights()
      this.gridApi.sizeColumnsToFit()
      this.isEditVisible = !this.isEditVisible
    },
    async onSaveGridClicked() {
      try {
        await this.updateStatsClassesStudentsDistribution(this.rowData)
        await this.fetchStatsClassesStudentsDistribution()
        this.pushToasts({
          title: 'Уведомление',
          message: 'Обновление численности учащихся произошло успешно'
        })
      } catch (error) {
        this.pushToasts({
          title: 'Ошибка',
          error: true,
          message: 'При обновлении численности учащихся произошла ошибка'
        })
        console.log(error)
      }
      this.columnDefs = this.columnDefs
        .map((col) => ({ ...col, editable: false }))
      await this.gridApi.resetRowHeights()
      this.gridApi.sizeColumnsToFit()
      this.isEditVisible = !this.isEditVisible
    },
    sortByCount(class1, class2) {
      return class2[this.getYear] - class1[this.getYear]
    },
    onColumnResized() {
      this.gridApi.resetRowHeights()
    },
    onGridReady(params) {
      this.gridApi = params.api
      this.gridApi.sizeColumnsToFit()
    },
    onGridSizeChanged() {
      this.gridApi.sizeColumnsToFit()
    },
    onRowDataChanged() {
      if (this.gridApi) {
        this.gridApi.sizeColumnsToFit()
      }
    }
  },
  computed: {
    ...mapGetters({
      orgs: 'dicts/getOrganizations',
      statsClassesStudentsDistribution: 'statistics/getStatsClassesStudentsDistribution'
    }),
    filteredStudentsClassesDistribution() {
      const unSortedData = _.cloneDeep(this.statsClassesStudentsDistribution)
      return unSortedData.sort(this.sortByCount)
    }
  }
}

</script>

<style>
  .ag-grid-for-count-stat {
    height: 700px;
    border-top: 1px solid #017ac3;
    border-bottom: 1px solid #017ac3;
    margin-left: 20px;
  }
  .ag-grid-for-count-stat .ag-header-cell-label .ag-header-cell-text {
    height: auto;
    white-space: normal;
    word-wrap: break-word;
    text-align: center;
  }
  .ag-grid-for-count-stat .ag-icon {
    color: #017ac3;
  }
  .ag-grid-for-count-stat .ag-header-viewport {
    border-bottom: 1px solid #017ac3;
  }
  .table-count-stats-header {
    font-size: 14px;
    padding: 10px !important;
    color: #017ac3;
  }
</style>
